/**
 * Deterministic, seedable PRNG utilities.
 *
 * Everything in the universe derives from string seeds hashed with xmur3 and
 * streamed through mulberry32, so client and server (and every player) agree
 * on the same universe without ever shipping world data over the wire.
 */

/** xmur3 string hash — produces a 32-bit seed function from a string. */
export function hashSeed(str: string): number {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  h = Math.imul(h ^ (h >>> 16), 2246822507);
  h = Math.imul(h ^ (h >>> 13), 3266489909);
  return (h ^= h >>> 16) >>> 0;
}

export type Rng = () => number;
/** Serializable continuation of the exact same stream, e.g. after worker work. */
export type StatefulRng = Rng & { state(): number };

/** mulberry32 — fast, decent-quality 32-bit PRNG. Returns floats in [0, 1). */
export function mulberry32(seed: number): StatefulRng {
  let a = seed >>> 0;
  const next = () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  return Object.assign(next, { state: () => a >>> 0 });
}

/** Convenience: PRNG seeded directly from a string. */
export function rngFromString(seed: string): StatefulRng {
  return mulberry32(hashSeed(seed));
}

export function rangeInt(rng: Rng, min: number, max: number): number {
  return min + Math.floor(rng() * (max - min + 1));
}

export function range(rng: Rng, min: number, max: number): number {
  return min + rng() * (max - min);
}

export function pick<T>(rng: Rng, items: readonly T[]): T {
  return items[Math.floor(rng() * items.length)];
}
